import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/apiClient'
import type {
  Expense,
  CreateExpenseBody,
  UpdateExpenseBody,
  ExpensesSummary,
  ExpensesListResponse,
  ExpenseView,
} from '@/types/backend'

type ListParams = {
  search?: string
  categoryId?: string
  vendorId?: string
  from?: string
  to?: string
  page?: number
  pageSize?: number
}

export const useExpensesListQuery = (params: ListParams, enabled = true) =>
  useQuery({
    queryKey: ['expenses', 'list', params],
    enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<ExpensesListResponse>('/expenses', {
        params,
      })
      return data
    },
  })

export const useExpenseQuery = (id?: string, enabled: boolean = !!id) =>
  useQuery({
    queryKey: ['expenses', 'one', id],
    enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<Expense>(`/expenses/${id}`)
      return data
    },
  })

export const useExpensesSummaryQuery = (
  from?: string,
  to?: string,
  enabled = true
) =>
  useQuery({
    queryKey: ['expenses', 'summary', { from, to }],
    enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<ExpensesSummary>(
        '/expenses/_summary',
        {
          params: { from, to },
        }
      )
      return data
    },
  })

export const useCreateExpenseMutation = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: CreateExpenseBody) => {
      const { data } = await apiClient.post<Expense>('/expenses', payload)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['expenses', 'list'] })
      qc.invalidateQueries({ queryKey: ['expenses', 'summary'] })
      qc.invalidateQueries({ queryKey: ['cash', 'summary'] }) // ⬅️ cash dépend des dépenses
    },
  })
}

export const useUpdateExpenseMutation = () => {
  const qc = useQueryClient()
  return useMutation<ExpenseView, unknown, { id: string } & UpdateExpenseBody>({
    mutationFn: async ({ id, ...payload }) => {
      const { data } = await apiClient.patch<Expense>(
        `/expenses/${id}`,
        payload
      )
      return data
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['expenses', 'one', vars.id] })
      qc.invalidateQueries({ queryKey: ['expenses', 'list'] })
      qc.invalidateQueries({ queryKey: ['expenses', 'summary'] })
      qc.invalidateQueries({ queryKey: ['cash', 'summary'] })
    },
  })
}

export const useDeleteExpenseMutation = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.delete<unknown>(`/expenses/${id}`)
      return data
    },
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ['expenses', 'one', id] })
      qc.invalidateQueries({ queryKey: ['expenses', 'list'] })
      qc.invalidateQueries({ queryKey: ['expenses', 'summary'] })
      qc.invalidateQueries({ queryKey: ['cash', 'summary'] })
    },
  })
}
